import type { RequestContext } from '@/domains/global/RequestContext';
import type {
  DocsSiteSummaryServerType,
  DocsSiteSummaryServerType4Domain,
} from '@/services/docs-site-summary/docsSiteSummary';
import type { SEOInfoServerType } from '@/services/seo-info/seoInfo';
import { Any } from '@/shared/constant/any';
import type { DocsBaseConfig } from '@/shared/constant/DocsBaseConfig';
import type { DocsIdTypeData } from '@/shared/constant/DocsIdTypeData';
import type { FooterBanner } from '@/shared/constant/footer-banner/FooterBanner';
import {
  loadEnvironmentsMap,
  loadSelectedEnvironmentIdByDocsId,
  saveEnvironmentsByDocsId,
  saveSelectedEnvironmentIdByDocsId,
} from '@/shared/constant/local-storage/use-environment/UseEnvironmentStorage';
import type { Navigation } from '@/shared/constant/navigation/Navigation';
import { NavigationTypeEnum } from '@/shared/constant/navigation/Navigation';
import type { Notification } from '@/shared/constant/notification/Notification';
import type { ResourceData } from '@/shared/constant/ResourceData';
import type { SidebarTree } from '@/shared/constant/sidebar-tree/SidebarTree';
import { StorageType } from '@/shared/constant/StorageType';
import type { VersionListItem } from '@/shared/constant/version-list/VersionListItem';
import { createContextStore } from '@/shared/util/createContextStore';
import { isServer } from '@/shared/util/ssr';
import treeListFilter from '@/shared/util/treeListFilter';
import { ModelType } from '@tarslib/types/lib/common';
import type { IEnvironment } from '@tarslib/types/lib/environment';
import type { CustomCodes } from '@tarslib/types/lib/publishedProject';
import cloneDeep from 'lodash-es/cloneDeep';
import { useMemo } from 'react';

export type DocsDataState = {
  navigation: Navigation;
  sidebarTree: SidebarTree;
  searchKeyword?: string | undefined;
  docsBaseConfig: DocsBaseConfig;
  versionList: VersionListItem[];
  resourceData: ResourceData;
  docsIdTypeData: DocsIdTypeData;
  notification: Notification[];
  footerBanner: FooterBanner[];
  projectSetting: DocsSiteSummaryServerType['summary']['projectSetting'];
  environments: DocsSiteSummaryServerType['summary']['environments'];
  searchSettings?: DocsSiteSummaryServerType4Domain['summary']['searchSettings'];
  versionSettings?: DocsSiteSummaryServerType4Domain['summary']['versionSettings'];
  seoInfos?: SEOInfoServerType;
  itemPathPrefix: ReturnType<RequestContext['getItemPathPrefix']>;
  appEnvMap: {
    appBrowserExtensionAdapterKey: string;
  };
  customCodes?: CustomCodes;
};

export type DocsDataAction = {
  getState: () => DocsDataState;
  selectEnvironment: (environmentId: number, withoutPersist?: boolean) => void;
  saveEnvironmentVariables: (environmentId: number, variables: IEnvironment['variables']) => void;
  getEnvironmentById: (environmentId: number) => IEnvironment | undefined;
  getCurrentEnvironment: () => IEnvironment | undefined;
  loadClientEnvironment: () => void;
  isClientEnvironmentLoaded?: boolean;
  changeSidebarTreeSearchKeyword: (searchKeyword: string) => void;
  isSearchVariablesInURLPrepared?: boolean;
  setSearchVariablesInURLPrepared: (isPrepared: boolean) => void;
};

export const docsDataInitialState: DocsDataState = {
  navigation: {
    type: NavigationTypeEnum.None,
  },
  sidebarTree: {
    goBackSidebarTreeApiFolderNode: undefined,
    rootSidebarTreeApiFolderNode: undefined,
    sidebarTreeList: [],
    selectedSidebarTreeNode: undefined,
    nextSidebarTreeNode: undefined,
    parentSidebarTreeApiFolderNodes: [],
    previousSidebarTreeNode: undefined,
  },
  notification: [],
  docsBaseConfig: {
    ...Any,
  },
  versionList: [],
  resourceData: {
    ...Any,
  },
  projectSetting: {
    ...Any,
  },
  ...Any,
};

function normalizePathForSearch(path: string): string {
  // Replace parameterized segments with a wildcard pattern
  return path.replace(/\{[^}]+\}/g, '[^/]+');
}

const [DocsDataModelStoreProvider, useDocsDataModelStore] = createContextStore<
  DocsDataState,
  DocsDataAction
>((set, get) => ({
  ...docsDataInitialState,
  getState: () => get(),
  selectEnvironment: (id: number, withoutPersist?: boolean) => {
    const environment = get().environments.environmentList.find((x) => x.id === id);
    if (!environment) {
      return;
    }
    set((state) => ({
      ...state,
      environments: {
        ...state.environments,
        selectedEnvironment: environment,
      },
    }));
    if (environment) {
      saveSelectedEnvironmentIdByDocsId(get().docsIdTypeData.onlineId, environment.id);
    }
    if (withoutPersist) {
      return;
    }
    saveEnvironmentsByDocsId(
      get().docsIdTypeData.onlineId,
      get().environments.environmentList,
      get().docsBaseConfig.persistEnvVariables ? StorageType.LocalStorage : StorageType.Memory,
    );
  },
  saveEnvironmentVariables: (environmentId: number, variables: IEnvironment['variables']) => {
    const newEnvironmentList = cloneDeep(get().environments.environmentList);
    const foundEnvironment = newEnvironmentList.find((x) => x.id === environmentId);
    if (!foundEnvironment) {
      return;
    }
    foundEnvironment.variables = variables;
    set((state) => ({
      ...state,
      environments: {
        ...state.environments,
        environmentList: newEnvironmentList,
      },
    }));
    saveEnvironmentsByDocsId(
      get().docsIdTypeData.onlineId,
      get().environments.environmentList,
      get().docsBaseConfig.persistEnvVariables ? StorageType.LocalStorage : StorageType.Memory,
    );
  },
  getEnvironmentById: (environmentId: number) => {
    return get().environments.environmentList.find((x) => x.id === environmentId);
  },
  getCurrentEnvironment: () => {
    get().loadClientEnvironment();
    return get().environments.selectedEnvironment;
  },
  loadClientEnvironment: () => {
    if (isServer) {
      return;
    }
    try {
      const lastSelectedId = loadSelectedEnvironmentIdByDocsId(get().docsIdTypeData.onlineId);
      if (lastSelectedId) {
        get().selectEnvironment(lastSelectedId, true);
      } else {
        const currentId = get().environments.selectedEnvironment?.id;
        if (currentId) {
          saveSelectedEnvironmentIdByDocsId(get().docsIdTypeData.onlineId, currentId);
        }
      }
      const localEnvironmentsMapStr = loadEnvironmentsMap(
        get().docsBaseConfig.persistEnvVariables ? StorageType.LocalStorage : StorageType.Memory,
      );
      if (!localEnvironmentsMapStr || localEnvironmentsMapStr === '{}') {
        return;
      }
      const localEnvironmentsMap: Record<
        string | number,
        {
          environmentList?: IEnvironment[];
        }
      > = JSON.parse(localEnvironmentsMapStr);
      if (!localEnvironmentsMap) {
        return;
      }
      const environmentsInLocal = localEnvironmentsMap[get().docsIdTypeData.onlineId];
      if (!environmentsInLocal) {
        return;
      }
      const localEnvironmentMap: Record<string, IEnvironment> = {};
      environmentsInLocal?.environmentList?.forEach((item: IEnvironment) => {
        localEnvironmentMap[item.id] = item;
      });
      const newEnvironmentList = cloneDeep(get().environments.environmentList);
      newEnvironmentList.forEach((item) => {
        const foundEnvironment = localEnvironmentMap[item.id];
        if (!foundEnvironment) {
          return;
        }
        const localVariables = foundEnvironment.variables;
        if (!localVariables) {
          return;
        }
        localVariables.forEach((varItem) => {
          const foundVariable = item.variables.find((resItem) => resItem.name === varItem.name);
          if (foundVariable) {
            foundVariable.value = varItem.value || foundVariable.value;
          } else {
            item.variables.push(varItem);
          }
        });
      });
      set((state) => ({
        ...state,
        environments: {
          ...state.environments,
          environmentList: newEnvironmentList,
        },
      }));
      saveEnvironmentsByDocsId(
        get().docsIdTypeData.onlineId,
        newEnvironmentList,
        get().docsBaseConfig.persistEnvVariables ? StorageType.LocalStorage : StorageType.Memory,
      );
    } catch (error) {
      console.warn('Failed to load client environment', error);
    } finally {
      set((state) => ({
        ...state,
        isClientEnvironmentLoaded: true,
      }));
    }
  },
  changeSidebarTreeSearchKeyword: (searchKeyword) => {
    if (!searchKeyword) {
      set((state) => ({
        ...state,
        searchKeyword: undefined,
      }));
      return;
    }
    set((state) => ({
      ...state,
      searchKeyword,
    }));
  },
  setSearchVariablesInURLPrepared: (isPrepared) => {
    set((state) => ({
      ...state,
      isSearchVariablesInURLPrepared: isPrepared,
    }));
  },
}));

export const useSearchableTreeList = () => {
  const sidebarTreeList = useDocsDataModelStore((x) => x.sidebarTree.sidebarTreeList);
  const searchKeyword = useDocsDataModelStore((x) => x.searchKeyword);
  const sidebarTreeListFiltered = useMemo(() => {
    if (!searchKeyword) {
      return sidebarTreeList;
    }
    return treeListFilter(sidebarTreeList, searchKeyword, {
      contentBuilder: (treeNode) => {
        if (treeNode.type === ModelType.HttpApi) {
          return `${treeNode.name} ${treeNode.method} ${
            treeNode.path
          } ${Array.isArray(treeNode.tags) ? treeNode.tags.join(' ') : ''}`;
        } else {
          return `${treeNode.name} ${Array.isArray(treeNode.tags) ? treeNode.tags.join(' ') : ''}`;
        }
      },
      skipFilterChildren: (treeNode) =>
        treeNode.type === ModelType.HttpApi ||
        treeNode.type === ModelType.WebSocketApi ||
        treeNode.type === ModelType.CUSTOM_ENDPOINT ||
        treeNode.type === ModelType.SOCKET_IO_API ||
        treeNode.type === ModelType.ApiSchema,
      childrenAttributeName: 'children',
      matchKeywordsCallback: (treeNode, content, keywordList) => {
        // First try direct matching
        if (keywordList.every((keyword) => content.indexOf(keyword) > -1)) {
          return true;
        }
        const treeNodePath = treeNode.type === ModelType.HttpApi ? String(treeNode.path) : '';
        // If direct matching fails and the content looks like a path with parameters,
        // try matching against a normalized version
        if (treeNodePath.includes('{') && treeNodePath.includes('}')) {
          const normalizedPath = normalizePathForSearch(treeNodePath);
          const regex = (() => {
            try {
              return new RegExp(normalizedPath, 'gi');
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (_ignored) {
              return null;
            }
          })();
          return Boolean(
            regex &&
              keywordList.every((keyword) => content.indexOf(keyword) > -1 || regex.test(keyword)),
          );
        }
        return false;
      },
    });
  }, [searchKeyword, sidebarTreeList]);

  return sidebarTreeListFiltered;
};

export { DocsDataModelStoreProvider, useDocsDataModelStore };
